import React, { MouseEvent, PureComponent } from 'react';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import defaultTo from 'lodash/defaultTo';
import { st, classes } from './Modal.st.css';
import { ariaDescribedBy, dataHooks, flexPositions, labels } from './constants';
import { ZIndex } from '../common/ZIndex';
import uniqueId from 'lodash/uniqueId';
import WixDesignSystemProvider from '../WixDesignSystemProvider';
import { ariaLabels } from '../common/accessibility/ariaLabels';
import CloseButton from '../CloseButton';
import IconButton from '../IconButton';
import { ArrowLeft, ArrowRight } from '@wix/wix-ui-icons-common';
import Tooltip from '../Tooltip';
import { ModalProps } from './Modal.types';

class Modal extends PureComponent<ModalProps> {
  static displayName = 'Modal';

  private CHILDREN_WRAPPER_DIV_ID: string;

  constructor(props: ModalProps) {
    super(props);

    this.CHILDREN_WRAPPER_DIV_ID = uniqueId('wsr-modal');
  }

  static propTypes = {
    dataHook: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    borderRadius: PropTypes.number,
    contentLabel: PropTypes.string,
    children: PropTypes.any,
    zIndex: PropTypes.number,
    shouldCloseOnOverlayClick: PropTypes.bool,
    shouldDisplayCloseButton: PropTypes.bool,
    onRequestClose: PropTypes.func,
    onAfterOpen: PropTypes.func,
    onAfterClose: PropTypes.func,
    horizontalPosition: PropTypes.oneOf(['start', 'center', 'end']),
    verticalPosition: PropTypes.oneOf(['start', 'center', 'end']),
    closeTimeoutMS: PropTypes.number,
    scrollable: PropTypes.bool,
    scrollableContent: PropTypes.bool,
    maxHeight: PropTypes.string,
    height: PropTypes.string,
    overlayPosition: PropTypes.oneOf([
      'static',
      'relative',
      'absolute',
      'fixed',
      'sticky',
    ]),
    parentSelector: PropTypes.func,
    appElement: PropTypes.string,
    screen: PropTypes.oneOf(['full', 'desktop', 'mobile']),
    showNavigationPreviousButton: PropTypes.bool,
    showNavigationNextButton: PropTypes.bool,
    onNavigationClickPrevious: PropTypes.func,
    onNavigationClickNext: PropTypes.func,
    navigationPreviousLabel: PropTypes.string,
    navigationNextLabel: PropTypes.string,
    transition: PropTypes.oneOf(['scale', 'moveHorizontal', 'moveVertical']),
  };

  static defaultProps = {
    borderRadius: 0,
    shouldCloseOnOverlayClick: false,
    shouldDisplayCloseButton: false,
    horizontalPosition: 'center',
    verticalPosition: 'center',
    closeTimeoutMS: 500,
    scrollable: true,
    scrollableContent: false,
    height: '100%',
    maxHeight: 'auto',
    overlayPosition: 'fixed',
    screen: 'desktop',
    transition: 'scale',
  } as const;

  handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    const { shouldCloseOnOverlayClick, onRequestClose } = this.props;
    if (
      shouldCloseOnOverlayClick &&
      (event.target as HTMLDivElement).id === this.CHILDREN_WRAPPER_DIV_ID &&
      onRequestClose
    ) {
      onRequestClose();
    }
  };

  renderCloseButton = () => {
    return (
      <CloseButton
        dataHook={dataHooks.modalCloseButton}
        className={classes.closeButton}
        onClick={this.props.onRequestClose}
        size="large"
        skin="light"
        aria-label={labels.close}
      />
    );
  };

  renderNavigationControls = () => {
    const DIRECTIONS = { prev: 'prev', next: 'next' } as const;
    const TOOLTIP_GAP = 9;
    const {
      showNavigationPreviousButton,
      showNavigationNextButton,
      onNavigationClickPrevious,
      onNavigationClickNext,
      navigationPreviousLabel,
      navigationNextLabel,
    } = this.props;
    const buildButton = (dir: 'prev' | 'next', className?: string) => {
      const isPrevious = dir === DIRECTIONS.prev;
      return (
        <IconButton
          className={className}
          priority="secondary"
          size="large"
          onClick={
            isPrevious ? onNavigationClickPrevious : onNavigationClickNext
          }
          dataHook={
            isPrevious
              ? dataHooks.navigationPrevButton
              : dataHooks.navigationNextButton
          }
        >
          {isPrevious ? <ArrowLeft /> : <ArrowRight />}
        </IconButton>
      );
    };
    return (
      <React.Fragment>
        {showNavigationPreviousButton &&
          (navigationPreviousLabel ? (
            <Tooltip
              className={classes.navPrev}
              content={navigationPreviousLabel}
              moveBy={{ y: TOOLTIP_GAP }}
              aria-describedby={ariaDescribedBy.navigationPrev}
              dataHook={dataHooks.navigationPrevTooltip}
            >
              {buildButton(DIRECTIONS.prev)}
            </Tooltip>
          ) : (
            buildButton(DIRECTIONS.prev, classes.navPrev)
          ))}
        {showNavigationNextButton &&
          (navigationNextLabel ? (
            <Tooltip
              className={classes.navNext}
              content={navigationNextLabel}
              moveBy={{ y: TOOLTIP_GAP }}
              aria-describedby={ariaDescribedBy.navigationNext}
              dataHook={dataHooks.navigationNextTooltip}
            >
              {buildButton(DIRECTIONS.next)}
            </Tooltip>
          ) : (
            buildButton(DIRECTIONS.next, classes.navNext)
          ))}
      </React.Fragment>
    );
  };

  render() {
    const {
      dataHook,
      horizontalPosition,
      verticalPosition,
      height,
      scrollableContent,
      borderRadius,
      zIndex,
      scrollable,
      isOpen,
      shouldCloseOnOverlayClick,
      shouldDisplayCloseButton,
      onRequestClose,
      onAfterClose,
      onAfterOpen,
      contentLabel,
      closeTimeoutMS,
      children,
      appElement,
      overlayPosition,
      parentSelector,
      screen,
      showNavigationPreviousButton,
      showNavigationNextButton,
      onNavigationClickPrevious,
      onNavigationClickNext,
      transition,
    } = this.props;

    let { maxHeight } = this.props;
    const justifyContent =
      // @ts-expect-error Typescript doesn't know about Modal.defaultProps
      flexPositions[horizontalPosition];
    const alignItems =
      // @ts-expect-error Typescript doesn't know about Modal.defaultProps
      flexPositions[verticalPosition];

    maxHeight = scrollableContent && maxHeight === 'auto' ? '100vh' : maxHeight;

    const modalStyles = {
      overlay: {
        // Overriding defaults
        position: overlayPosition,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: defaultTo(zIndex, ZIndex.modal),
        backgroundColor: undefined, // undefined disables the property, use css instead
        // Overriding defaults - END
        display: 'flex',
        justifyContent,
        alignItems,
        overflowY:
          scrollable && transition !== 'moveVertical' ? 'auto' : 'clip',
        overflowX: transition === 'moveHorizontal' ? 'clip' : undefined,
      },
      content: {
        // Overriding defaults
        border: 'none',
        overflowY: scrollableContent ? 'auto' : 'initial',
        overflowX: scrollableContent ? 'hidden' : 'initial',
        height,
        maxHeight,
        width: '100%',
        // stylelint-disable-line
        WebkitOverflowScrolling: 'touch',
        outline: 'none',
        borderRadius,
        padding: '0px',
        // Overriding defaults - END
        backgroundColor: 'transparent',
        marginBottom: '0px',
        position: 'relative',
      },
    } as const;

    if (appElement) {
      ReactModal.setAppElement(appElement);
    } else {
      ReactModal.setAppElement('body');
    }

    return (
      <div
        data-hook={dataHook}
        onKeyDown={e => {
          const arrows = {
            left: 'ArrowLeft',
            right: 'ArrowRight',
          };
          if (e && e.key === arrows.left) {
            showNavigationPreviousButton &&
              onNavigationClickPrevious &&
              onNavigationClickPrevious();
          }
          if (e && e.key === arrows.right) {
            showNavigationNextButton &&
              onNavigationClickNext &&
              onNavigationClickNext();
          }
        }}
      >
        <ReactModal
          portalClassName={st(
            classes.root,
            { scrollable, transition },
            `portal portal-${dataHook}`,
          )}
          isOpen={isOpen}
          shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
          onRequestClose={onRequestClose}
          onAfterOpen={onAfterOpen}
          onAfterClose={onAfterClose}
          style={modalStyles}
          className={classes.modal}
          closeTimeoutMS={closeTimeoutMS}
          parentSelector={parentSelector}
          ariaHideApp={false}
          contentLabel={contentLabel}
          aria={{
            labelledby: ariaLabels.labelledBy,
            describedby: ariaLabels.describedBy,
          }}
        >
          <WixDesignSystemProvider>
            {isOpen && shouldDisplayCloseButton && this.renderCloseButton()}

            <div
              data-scrollable={scrollable || null}
              id={this.CHILDREN_WRAPPER_DIV_ID}
              className={st(classes.childrenContainer, {
                screen,
              })}
              onClick={this.handleOverlayClick}
            >
              {(showNavigationPreviousButton || showNavigationNextButton) &&
                this.renderNavigationControls()}
              {children}
            </div>
          </WixDesignSystemProvider>
        </ReactModal>
      </div>
    );
  }
}

export default Modal;
